import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, Observable } from 'rxjs';
import { MENU_ITEMS } from './menu';
import { MenuChildren, MenuItem } from '@models/common.model';
import { isEmpty } from 'lodash';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { selectPermissions } from '@app/store/selectors/common.selector';

@Component({
    selector: 'pn-menu',
    templateUrl: './menu.component.html',
    styleUrl: './menu.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandCollapse', [
            state('expanded', style({ height: '*' })),
            state('collapsed', style({ height: 0 })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class MenuComponent implements OnInit {
    private _router = inject(Router);
    private _cd = inject(ChangeDetectorRef);
    private store = inject(Store<StoreModel>);

    public expanded = input<boolean>(true);
    public overlayVisible = input<boolean>(false);
    private permissions$ = this.store.select(selectPermissions);
    public menuItems$ = this.filterMenuItemByPermission();
    private updateMenu$ = new BehaviorSubject<Record<string, string | number | boolean>>({});
    public menuItems = combineLatest({
        menu: this.menuItems$,
        update: this.updateMenu$.asObservable(),
    }).pipe(
        debounceTime(0),
        map(({ menu, update }) => {
            if (isEmpty(update)) {
                return menu;
            }

            return menu.map((m) => ({
                ...m,
                ...(m.name === update['name'] && update),
                ...(!update['name'] && update),
            }));
        })
    );
    // public menuItems = MENU_ITEMS;
    public cdkConnectedOverlayPositions: ConnectedPosition[] = [
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 63,
            offsetY: -15,
        },
    ];
    public expandedMenu: string | null = null;
    public showPopoverChildren: string | null = null;
    public currentUrl = this._router.url;

    ngOnInit(): void {
        this._router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            this.currentUrl = this._router.url;
            this._cd.detectChanges();
        });
    }

    /**
     * Handle expand menu
     *
     * @param {MenuItem} item
     * @return {*}
     * @memberof SidebarComponent
     */
    public handleExpandMenu(item: MenuItem): void {
        if (!this.expanded()) {
            return;
        }
        this.expandedMenu = item.name === this.expandedMenu ? null : item.name;
    }

    /**
     * Handle show menu children
     *
     * @param {MenuItem} item
     * @return {*}
     * @memberof SidebarComponent
     */
    public handleShowChildren(item: MenuItem): void {
        if (this.expanded()) {
            return;
        }
        this.showPopoverChildren = item.name;
        this.updateMenu$.next({
            name: item.name,
            showChildren: item.children?.length ? !item.showChildren : false,
        });
    }

    /**
     * Handle backdrop click
     *
     * @memberof SidebarComponent
     */
    public backdropClick(): void {
        this.showPopoverChildren = null;
        this.updateMenu$.next({
            showChildren: false,
        });
    }

    /**
     * Track by name
     *
     * @param index
     * @param item
     * @return {string}
     */
    public trackParentByName(index: number, item: MenuItem): string {
        return item.name;
    }

    /**
     * track by link
     *
     * @param {number} index
     * @param {MenuChildren} item
     * @return {*}
     * @memberof SidebarComponent
     */
    public trackChildByLink(index: number, item: MenuChildren): string {
        return item.link;
    }

    private filterMenuItemByPermission(): Observable<MenuItem[]> {
        return this.permissions$.pipe(
            map((permissions) => {
                const findPermission = (c?: string, a?: string) => {
                    return permissions.find((p) => p.ControllerName === c && p.ActionName === a);
                };

                if (!permissions.length) {
                    return [];
                }
                return MENU_ITEMS.filter((menu) => {
                    if (menu.controller && menu.actionName) {
                        return findPermission(menu.controller, menu.actionName)?.IsShowMenu;
                    }
                    return true;
                })
                    .map((m) => {
                        let children: MenuChildren[] = [];
                        if (m.children?.length) {
                            children = (m.children as MenuChildren[]).filter((c) => {
                                if (c.actionName && c.controller) {
                                    const find = findPermission(c.controller, c.actionName);
                                    return find?.IsShowMenu;
                                }
                                return true;
                            });
                        }

                        return {
                            ...m,
                            children,
                            expand: m.expand ?? true,
                            showChidren: false,
                        };
                    })
                    .filter((v) => v.link || !!v.children?.length);
            })
        );
    }
}
